import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAppFilters } from "../state/filters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { formatInt, formatMoney, formatPct } from "../lib/format";
import { Megaphone, Save } from "lucide-react";
import ExportButton from "../components/ExportButton";

export default function MarketingLeads() {
  const { range } = useAppFilters();
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [mp, sc] = await Promise.all([
        api.get("/dashboard/marketing", { params: { start: range.start, end: range.end } }),
        api.get("/settings/source-costs"),
      ]);
      setData(mp.data);
      const m = {};
      for (const it of sc.data.items || []) m[it.source] = it.monthly_cost;
      setCosts(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range]);

  const saveCost = async (src) => {
    try {
      await api.post("/settings/source-costs", { source: src, monthly_cost: Number(costs[src] || 0) });
      toast.success(`Cost saved for ${src}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="page-marketing">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Marketing & Lead Performance</h1>
          <p className="text-sm text-muted-foreground">Leads, sales, ROI by source.</p>
        </div>
        <ExportButton path="/export/marketing.csv" params={{ start: range.start, end: range.end }} filename="marketing_roi.csv" testid="marketing-export-csv" />
      </div>

      <Card data-testid="card-source-roi">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium"><Megaphone size={16} /> Source ROI</CardTitle>
          <span className="text-xs text-muted-foreground">Enter monthly cost to calculate ROI</span>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Avg ticket</TableHead>
                  <TableHead className="text-right">Close rate</TableHead>
                  <TableHead className="text-right w-[160px]">Monthly cost</TableHead>
                  <TableHead className="text-right">CPL</TableHead>
                  <TableHead className="text-right">CPS</TableHead>
                  <TableHead className="text-right">ROI</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.sources || []).map((s) => (
                  <TableRow key={s.source} className="row-hover" data-testid={`marketing-row-${s.source.replaceAll(" ", "-").toLowerCase()}`}>
                    <TableCell className="font-medium">{s.source}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInt(s.leads)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInt(s.sales)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(s.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(s.avg_ticket)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatPct(s.close_rate)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          type="number"
                          className="h-8 w-24 text-right tabular-nums"
                          value={costs[s.source] ?? 0}
                          onChange={(e) => setCosts({ ...costs, [s.source]: e.target.value })}
                          data-testid={`source-cost-input-${s.source.replaceAll(" ", "-").toLowerCase()}`}
                        />
                        <Button size="icon" variant="ghost" onClick={() => saveCost(s.source)} data-testid={`source-cost-save-${s.source.replaceAll(" ", "-").toLowerCase()}`}><Save size={14} /></Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(s.cpl)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(s.cps)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${s.roi > 0 ? "delta-positive" : s.roi < 0 ? "delta-negative" : ""}`}>{s.cost > 0 ? `${s.roi}%` : "–"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
